import { useCallback, useEffect, useState } from 'react';
import type { ContactDto, SaveContactBody } from '@chat-emp/shared';

import { ApiRequestError } from '../lib/session.js';
import { deleteContact, listContacts, saveContact } from './api.js';

export interface UseContacts {
  contacts: ContactDto[];
  loading: boolean;
  error?: string;
  /** New contacts go first. */
  save: (body: SaveContactBody) => Promise<ContactDto>;
  remove: (id: string) => Promise<void>;
  /** Loads the book again, e.g. after a failed load. */
  reload: () => void;
}

const UNAVAILABLE = 'Contacts are unavailable right now.';

/** What to tell the employer about a failed call: the BFF's sentence, or `fallback`. */
export function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiRequestError ? err.message : fallback;
}

/** `list` with each of `rows` replacing its namesake in place, or put first when new. */
function upsert(list: ContactDto[], rows: ContactDto[]): ContactDto[] {
  const byId = new Map(rows.map((c) => [c.id, c]));
  const known = new Set(list.map((c) => c.id));
  return [...rows.filter((c) => !known.has(c.id)), ...list.map((c) => byId.get(c.id) ?? c)];
}

/** The employer's contact book (Contacts PoC). Fetches nothing while `enabled` is false. */
export function useContacts(enabled: boolean): UseContacts {
  const [contacts, setContacts] = useState<ContactDto[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setLoading(true);
    setError(undefined);
    listContacts()
      // Rows saved while this was in flight stay; the server's copy wins for the rest.
      .then((list) => live && setContacts((local) => upsert(list, local)))
      .catch((err: unknown) => live && setError(messageOf(err, UNAVAILABLE)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
      setLoading(false);
    };
  }, [enabled, attempt]);

  const save = useCallback(async (body: SaveContactBody) => {
    const saved = await saveContact(body);
    setContacts((list) => upsert(list, [saved]));
    return saved;
  }, []);

  const remove = useCallback(async (id: string) => {
    try {
      await deleteContact(id);
    } catch (err) {
      // Already gone is the outcome the employer asked for.
      if (!(err instanceof ApiRequestError && err.code === 'contact_not_found')) throw err;
    }
    setContacts((list) => list.filter((c) => c.id !== id));
  }, []);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { contacts, loading, error, save, remove, reload };
}
